import type { RendererProps } from "../core/element-registry";
import { useRenderContext } from "../core/render-context";
import { useAtomDefaults } from "../contexts/atom-defaults-context";
import { ElementRenderer } from "./element-renderer";
import { StackedChildrenContext, declaresVerticalStack } from "./stacked-children";
import { cn } from "../lib/utils";
import type { CogoElement, CogoLayout } from "../core/types";
import { resolveInteractionClasses } from "../runtime/interaction-resolver";
import { paddingClasses, spacingToken } from "../lib/design-tokens";
import {
  useContainerBreakpoint,
  resolveResponsiveClassName,
} from "../contexts/breakpoint-context";
import { resolveStyleTokenValue } from "../lib/style-token-resolver";
import {
  ParentActivationProxy,
  normalizeParentActivationHostTag,
  parentActivationHostStyle,
  PARENT_ACTIVATION_HOST_CLASSES,
  resolveParentActivationLabel,
} from "./parent-activation";

// Standard Tailwind min-width breakpoints (mirrors breakpoint-context.tsx)
const TAILWIND_BP: Record<string, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

export function ContainerRenderer({
  element,
  children: propChildren,
}: RendererProps) {
  const {
    resolveSymbol,
    dispatchEvent,
    hasEventBinding,
    itemContext,
    designTokens,
  } = useRenderContext();
  const containerWidth = useContainerBreakpoint();
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: container.variant carries a semantic role (header/card/section) that
  // the existing variantClasses / header-branch logic below continues to use
  // unchanged. The data-* surface exposes the resolved fallback for external
  // assertion, same as the Task 6 button contract.
  const atomDefaults = useAtomDefaults("container");
  const resolvedVariant =
    element.variant ?? atomDefaults.defaultVariant ?? "default";
  const resolvedSize =
    (element as { size?: string }).size ??
    atomDefaults.defaultSize ??
    "default";
  const atomDataAttrs: Record<string, string | number | undefined> = {
    "data-atom-type": "container" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  };
  const sourceDataAttrs = dataAttributesFor(element);
  const rawDataState = (element as unknown as { dataState?: string }).dataState;
  const rawDataActive = (
    element as unknown as { dataActive?: boolean | string }
  ).dataActive;
  if (rawDataState !== undefined) sourceDataAttrs["data-state"] = rawDataState;
  if (rawDataActive !== undefined)
    sourceDataAttrs["data-active"] = rawDataActive;

  // Merge element-level responsive into layout for resolveLayoutClasses
  const effectiveLayout = (() => {
    const resp = element.responsive as
      | Record<string, Record<string, unknown>>
      | undefined;
    if (!resp || !element.layout) return element.layout;
    // element.responsive: { "sm": { "layout": { "columns": 1 } } }
    // → merge into layout.responsive: { "sm": { "columns": 1 } }
    const layoutResp: Record<string, Record<string, unknown>> = {};
    for (const [bp, overrides] of Object.entries(resp)) {
      const layoutOverrides = (overrides as Record<string, unknown>).layout as
        | Record<string, unknown>
        | undefined;
      if (layoutOverrides) {
        layoutResp[bp] = layoutOverrides;
      }
    }
    if (Object.keys(layoutResp).length === 0) return element.layout;
    return {
      ...element.layout,
      responsive: {
        ...((element.layout as unknown as Record<string, unknown>)
          .responsive as object),
        ...layoutResp,
      },
    } as CogoLayout;
  })();

  const layoutClasses = resolveLayoutClasses(effectiveLayout, containerWidth);
  const spacingClasses = element.padding ? paddingClasses(element.padding) : "";
  const gapClasses = element.gap ? `gap-${spacingToken(element.gap)}` : "";
  // Build inline styles from styling properties
  // Detect when className already handles specific CSS properties via Tailwind utilities.
  // Bridge (rehype) pipeline generates BOTH className AND structured options for the same properties.
  // Inline styles always beat Tailwind classes (highest CSS specificity), which breaks responsive
  // overrides like lg:max-w-none being overridden by inline max-width: 672px.
  const cls = element.className ?? "";
  const clsHandlesMaxWidth = /\bmax-w-/.test(cls);
  const clsHandlesBg = /\bbg-(?!transparent\b|inherit\b|current\b)/.test(cls);
  const clsHandlesOpacity = /\bopacity-/.test(cls);

  const styling = element.styling as Record<string, unknown> | undefined;
  const flatStyleProperties = element as unknown as {
    background?: unknown;
    border?: unknown;
    opacity?: unknown;
    alignment?: unknown;
    aspectRatio?: unknown;
    display?: unknown;
    gridCols?: unknown;
    minHeight?: unknown;
    overflow?: unknown;
  };
  // The ontology exposes background/border/opacity as flat container
  // properties. Keep the existing structured styling object authoritative
  // when both forms are present, and use the flat form as the canonical
  // fallback for generated Cogo JSON.
  const effectiveBackground =
    styling?.background ?? flatStyleProperties.background;
  const effectiveBorder = styling?.border ?? flatStyleProperties.border;
  const effectiveOpacity = styling?.opacity ?? flatStyleProperties.opacity;
  const effectiveAspectRatio =
    styling?.aspectRatio ?? flatStyleProperties.aspectRatio;
  const effectiveMinHeight =
    styling?.minHeight ?? flatStyleProperties.minHeight;
  const effectiveOverflow = styling?.overflow ?? flatStyleProperties.overflow;
  const effectiveDisplay =
    normalizeDisplay(effectiveLayout?.type) ??
    normalizeDisplay(flatStyleProperties.display);
  const effectiveGridCols =
    effectiveLayout?.columns ??
    normalizeGridColumns(flatStyleProperties.gridCols);
  const responsiveGridColumns: Record<string, number> = {};
  for (const [breakpoint, overrides] of Object.entries(
    effectiveLayout?.responsive ?? {},
  )) {
    const columns = normalizeGridColumns(
      (overrides as Record<string, unknown>).columns,
    );
    if (columns != null) responsiveGridColumns[breakpoint] = columns;
  }
  const hasResponsiveGridColumns =
    Object.keys(responsiveGridColumns).length > 0;
  const effectiveAlignment = normalizeAlignment(flatStyleProperties.alignment);
  const runtimeDisplay =
    effectiveDisplay ??
    (effectiveGridCols != null ? "grid" : undefined) ??
    (effectiveAlignment ? "flex" : undefined);

  if (effectiveAlignment) atomDataAttrs["data-alignment"] = effectiveAlignment;
  if (effectiveAspectRatio != null)
    atomDataAttrs["data-aspect-ratio"] = String(effectiveAspectRatio);
  if (runtimeDisplay) atomDataAttrs["data-display"] = runtimeDisplay;
  if (effectiveGridCols != null)
    atomDataAttrs["data-grid-cols"] = effectiveGridCols;
  if (hasResponsiveGridColumns) {
    atomDataAttrs["data-grid-cols-responsive"] = serializeRuntimeValue(
      responsiveGridColumns,
    );
  }
  if (effectiveLayout != null)
    atomDataAttrs["data-layout"] = serializeRuntimeValue(effectiveLayout);
  if (effectiveMinHeight != null)
    atomDataAttrs["data-min-height"] = String(effectiveMinHeight);
  if (effectiveOverflow != null)
    atomDataAttrs["data-overflow"] = String(effectiveOverflow);
  if (element.padding != null)
    atomDataAttrs["data-padding"] = serializeRuntimeValue(element.padding);

  // Build dynamic Tailwind arbitrary-value classes for simple styling props
  // (avoids inline styles that block responsive overrides)
  const dynamicClasses: string[] = [];
  if (element.maxWidth && !clsHandlesMaxWidth) {
    const mw =
      typeof element.maxWidth === "number"
        ? `${element.maxWidth}px`
        : String(element.maxWidth);
    dynamicClasses.push(`max-w-[${mw}]`);
  }
  if (styling?.minHeight)
    dynamicClasses.push(
      `min-h-[${normalizeCssLength(styling.minHeight) ?? styling.minHeight}]`,
    );
  if (styling?.width) {
    const w =
      typeof styling.width === "number"
        ? `${styling.width}px`
        : String(styling.width);
    dynamicClasses.push(`w-[${w}]`);
  }
  if (styling?.height) {
    const h =
      typeof styling.height === "number"
        ? `${styling.height}px`
        : String(styling.height);
    dynamicClasses.push(`h-[${h}]`);
  }
  if (styling?.maxWidth && !clsHandlesMaxWidth) {
    const smw =
      typeof styling.maxWidth === "number"
        ? `${styling.maxWidth}px`
        : String(styling.maxWidth);
    dynamicClasses.push(`max-w-[${smw}]`);
  }
  if (effectiveAspectRatio) {
    const ar = normalizeAspectRatio(effectiveAspectRatio);
    dynamicClasses.push(`aspect-[${ar}]`);
  }
  if (effectiveOverflow === "hidden") dynamicClasses.push("overflow-hidden");
  if (styling?.flex != null)
    dynamicClasses.push(`flex-[${Number(styling.flex)}]`);

  const inlineStyle: React.CSSProperties = {};
  if (runtimeDisplay) inlineStyle.display = runtimeDisplay;
  if (
    effectiveGridCols != null &&
    effectiveGridCols > 0 &&
    !hasResponsiveGridColumns
  ) {
    inlineStyle.gridTemplateColumns = `repeat(${effectiveGridCols}, minmax(0, 1fr))`;
  }
  const layoutGap = normalizeLayoutGap(effectiveLayout?.gap);
  if (layoutGap) inlineStyle.gap = layoutGap;
  if (effectiveMinHeight != null) {
    const minHeight = normalizeCssLength(effectiveMinHeight);
    if (minHeight) inlineStyle.minHeight = minHeight;
  }
  if (effectiveAspectRatio != null) {
    const aspectRatio = normalizeAspectRatio(effectiveAspectRatio);
    if (aspectRatio) inlineStyle.aspectRatio = aspectRatio;
  }
  if (effectiveOverflow != null && isOverflowValue(effectiveOverflow)) {
    inlineStyle.overflow = effectiveOverflow;
  }
  const paddingStyle = resolvePaddingStyle(element.padding);
  if (paddingStyle) inlineStyle.padding = paddingStyle;

  const nestedAlignment =
    effectiveLayout?.align != null || effectiveLayout?.justify != null;
  if (effectiveAlignment && !nestedAlignment) {
    const alignmentStyle = resolveAlignmentStyle(
      effectiveAlignment,
      effectiveLayout?.direction,
    );
    if (runtimeDisplay === "flex") {
      inlineStyle.flexDirection = effectiveLayout?.direction ?? "column";
    }
    Object.assign(inlineStyle, alignmentStyle);
  }
  if (effectiveOpacity != null && !clsHandlesOpacity) {
    const rawOpacity =
      typeof effectiveOpacity === "string" && effectiveOpacity.includes("#{")
        ? resolveSymbol(effectiveOpacity)
        : effectiveOpacity;
    const opacity = Number(rawOpacity);
    if (Number.isFinite(opacity))
      inlineStyle.opacity = Math.max(0, Math.min(1, opacity));
  }
  // Resolve background from styling.background (color or gradient)
  if (effectiveBackground && !clsHandlesBg) {
    const bg = effectiveBackground as Record<string, unknown>;
    if (bg.color) {
      const rawBg =
        typeof bg.color === "string" && bg.color.includes("#{")
          ? resolveSymbol(String(bg.color))
          : bg.color;
      const resolvedBg = String(
        resolveStyleTokenValue(rawBg, designTokens, "backgroundColor") ?? "",
      );
      if (resolvedBg.startsWith("#") && resolvedBg.length >= 7) {
        if (bg.opacity != null && Number(bg.opacity) < 1) {
          // Convert hex + opacity → rgba
          const hex = resolvedBg.replace("#", "");
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          inlineStyle.backgroundColor = `rgba(${r},${g},${b},${Number(bg.opacity)})`;
        } else {
          inlineStyle.backgroundColor = resolvedBg;
        }
      } else if (resolvedBg === "transparent") {
        inlineStyle.backgroundColor = "transparent";
      } else if (resolvedBg) {
        // Accept CSS variable references (hsl(var(--card))), rgb(), named colors, etc.
        // Auto-wrap bare var(--token) in hsl() — shadcn CSS variables store raw hsl channels
        if (
          /^var\(--[\w-]+\)$/.test(resolvedBg) &&
          !resolvedBg.startsWith("hsl(")
        ) {
          inlineStyle.backgroundColor = `hsl(${resolvedBg})`;
        } else {
          inlineStyle.backgroundColor = resolvedBg;
        }
      }
    }
    if (bg.gradient) {
      const grad = bg.gradient as Record<string, unknown>;
      const stops = grad.stops as Array<Record<string, unknown>> | undefined;
      if (stops && stops.length >= 2) {
        const colorStops = stops.map((s) => {
          const rawColor = String(s.color ?? "");
          const resolvedSymbolColor = rawColor.includes("#{")
            ? resolveSymbol(rawColor)
            : rawColor;
          const resolved = String(
            resolveStyleTokenValue(
              resolvedSymbolColor,
              designTokens,
              "backgroundColor",
            ) ?? "",
          );
          const opacity = s.opacity != null ? Number(s.opacity) : 1;
          const pos = s.position != null ? `${s.position}%` : "";
          if (opacity < 1 && resolved.startsWith("#")) {
            // Convert hex + opacity → rgba
            const hex = resolved.replace("#", "");
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return `rgba(${r},${g},${b},${opacity}) ${pos}`.trim();
          }
          return `${resolved} ${pos}`.trim();
        });
        if (grad.type === "radial") {
          inlineStyle.background = `radial-gradient(circle, ${colorStops.join(", ")})`;
        } else {
          const dirMap: Record<string, string> = {
            "to-bottom": "to bottom",
            "to-right": "to right",
            "to-bottom-right": "to bottom right",
            "to-top": "to top",
          };
          const dir =
            dirMap[String(grad.direction ?? "to-bottom")] ?? "to bottom";
          inlineStyle.background = `linear-gradient(${dir}, ${colorStops.join(", ")})`;
        }
      }
    }
  }
  // Resolve border color from styling.border.color (theme tokens)
  if (effectiveBorder) {
    const border = effectiveBorder as Record<string, unknown>;
    if (border.color) {
      const rawBorderColor =
        typeof border.color === "string" && border.color.includes("#{")
          ? resolveSymbol(String(border.color))
          : border.color;
      const resolvedBorderColor = String(
        resolveStyleTokenValue(rawBorderColor, designTokens, "borderColor") ??
          "",
      );
      if (resolvedBorderColor) {
        inlineStyle.borderColor = resolvedBorderColor;
      }
    }
    if (border.width && Number(border.width) >= 1) {
      inlineStyle.borderWidth = `${border.width}px`;
      inlineStyle.borderStyle = "solid";
    }
  }
  // Resolve element.transform → inline CSS transform
  if (element.transform) {
    const t = element.transform;
    const parts: string[] = [];
    if (t.scale != null) parts.push(`scale(${t.scale})`);
    else if (t.scaleX != null || t.scaleY != null)
      parts.push(`scale(${t.scaleX ?? 1}, ${t.scaleY ?? 1})`);
    if (t.rotate != null) parts.push(`rotate(${t.rotate}deg)`);
    if (t.translateX != null) parts.push(`translateX(${t.translateX}px)`);
    if (t.translateY != null) parts.push(`translateY(${t.translateY}px)`);
    if (t.skewX != null) parts.push(`skewX(${t.skewX}deg)`);
    if (t.skewY != null) parts.push(`skewY(${t.skewY}deg)`);
    if (parts.length) inlineStyle.transform = parts.join(" ");
    if (t.origin) inlineStyle.transformOrigin = t.origin;
  }

  // Resolve element.transition → inline CSS transition
  if (element.transition) {
    const tr = element.transition;
    const props = tr.properties?.join(", ") ?? "all";
    const dur = `${tr.duration ?? 150}ms`;
    const ease = tr.curve ?? "ease";
    inlineStyle.transition = `${props} ${dur} ${ease}${tr.delay ? ` ${tr.delay}ms` : ""}`;
  }

  // Merge element.style (low priority) — computed styles from styling.* take precedence
  if (element.style) {
    for (const [k, v] of Object.entries(element.style)) {
      if (!(k in inlineStyle)) {
        (inlineStyle as Record<string, unknown>)[k] = resolveStyleTokenValue(
          v,
          designTokens,
          k,
        );
      }
    }
  }

  const hasInlineStyle = Object.keys(inlineStyle).length > 0;

  // Resolve variant — props-first, no inline Tailwind class maps.
  // "card" variant: Normalizer converts to type="card" → CardRenderer handles it.
  // "header" variant: handled in the dedicated <header> render path below.
  // Other variants: use cn-container-variant-* classes from @cogo/styles.
  const variantClasses = (() => {
    if (!element.variant) return "";
    if (element.variant === "header") {
      // Only apply default header styling when the JSON doesn't provide its own className
      return element.className
        ? "sticky top-0 z-50 w-full border-b"
        : "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60";
    }
    return "";
  })();

  // Resolve position — support all CSS position types + auto-relative if any child is absolute
  const hasAbsoluteChild =
    element.children?.some((c) => c.position?.type === "absolute") ?? false;
  const positionClasses = (() => {
    const posType = element.position?.type;
    const classes: string[] = [];

    if (posType === "sticky") classes.push("sticky");
    else if (posType === "fixed") classes.push("fixed");
    else if (posType === "absolute") classes.push("absolute");
    else if (posType === "relative" || hasAbsoluteChild)
      classes.push("relative");

    // Apply top/right/bottom/left offset values from position
    if (posType && posType !== "relative") {
      const pos = element.position!;
      if (pos.top != null) classes.push(`top-${pos.top}`);
      if (pos.right != null) classes.push(`right-${pos.right}`);
      if (pos.bottom != null) classes.push(`bottom-${pos.bottom}`);
      if (pos.left != null) classes.push(`left-${pos.left}`);
    }

    return classes.join(" ");
  })();

  // Resolve interaction classes via typed InteractionConfig resolver
  const interactionClasses = resolveInteractionClasses(element.interaction);

  // Resolve tap event → onClick
  const tapHandler = element.events?.tap;
  const hasTap =
    Boolean(tapHandler) || (hasEventBinding?.(element.id, "tap") ?? false);
  const isNavigateAction =
    typeof tapHandler === "object" &&
    tapHandler.action === "navigate" &&
    tapHandler.params;
  const navigateRoute = isNavigateAction
    ? String(resolveSymbol(tapHandler.params!.route as string) ?? "")
    : undefined;
  const hasResolvedNavigateRoute = Boolean(isNavigateAction && navigateRoute);
  const usesActivationProxy = hasTap;
  const activationLabel = resolveParentActivationLabel(element, resolveSymbol);

  const handleClick = hasTap
    ? (e: React.MouseEvent) => {
        if (isNavigateAction && navigateRoute) {
          e.preventDefault();
          // Dispatch navigate event with resolved route and item context
          dispatchEvent(element.id, "tap", {
            __resolvedRoute: navigateRoute,
            ...itemContext,
          });
        } else {
          // Handle both string ("flowId") and object ({ flow: "flowId", params }) bindings
          dispatchEvent(element.id, "tap", itemContext);
        }
      }
    : undefined;

  // Fill property for buttons — flex-1
  const fillClasses = element.fill ? "flex-1" : "";

  // Margin passthrough (Tailwind margin classes like mb-2, mt-1)
  // Guard: if margin is still an object (missed by normalizeElement), skip it to prevent clsx bugs
  const marginClasses =
    typeof element.margin === "string" ? element.margin : "";

  // Grid col-span (for grid children)
  // Note: gridSpan { column, row } is now handled in normalizeElement → className
  const colSpanClasses = (() => {
    if (!element.colSpan) return "";
    if (typeof element.colSpan === "number")
      return `col-span-${element.colSpan}`;
    const raw = Object.entries(element.colSpan)
      .map(([bp, span]) =>
        bp === "default" ? `col-span-${span}` : `${bp}:col-span-${span}`,
      )
      .join(" ");
    return containerWidth !== Infinity
      ? resolveResponsiveClassName(raw, containerWidth)
      : raw;
  })();

  const className = cn(
    layoutClasses,
    spacingClasses,
    gapClasses,
    variantClasses,
    positionClasses,
    interactionClasses,
    fillClasses,
    marginClasses,
    colSpanClasses,
    ...dynamicClasses,
    element.className,
  );

  // Use prop children (from IteratedContainer) or element.children
  const renderedChildren =
    propChildren ??
    element.children?.map((child, idx) => (
      <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
    ));
  // Tell the direct children whether this container spaces them vertically, so
  // an atom that would default to inline can render as a block instead. The
  // value is provided UNCONDITIONALLY, including `false`: a non-stacking
  // container nested inside a stacked one must reset the answer for its own
  // subtree, or an inline row inside a column would be broken apart.
  const children = (
    <StackedChildrenContext.Provider value={declaresVerticalStack(className)}>
      {renderedChildren}
    </StackedChildrenContext.Provider>
  );
  const activationProxy = usesActivationProxy ? (
    <ParentActivationProxy
      elementId={element.id}
      href={hasResolvedNavigateRoute ? navigateRoute : undefined}
      label={activationLabel}
      onClick={handleClick!}
    />
  ) : null;
  const activationHostStyle = usesActivationProxy
    ? parentActivationHostStyle(
        element,
        hasInlineStyle ? inlineStyle : undefined,
      )
    : hasInlineStyle
      ? inlineStyle
      : undefined;
  const activationDomAttrs = domAttributesFor(element, usesActivationProxy);

  // Header variant: wrap children in a max-w container
  // Preserve element.className on the outer <header> so classes like `group` are
  // emitted on the DOM (required for group-hover: utilities on child elements).
  if (element.variant === "header") {
    // If the block already has its own flex layout (e.g., "flex items-center justify-between px-4 h-16"),
    // skip the inner wrapper to avoid double layout/padding.
    // NOTE: className check is intentional here — in Cogo JSON, className IS the data
    // (Tailwind classes are the layout specification), so inspecting it is reading props, not sniffing behavior.
    const hasOwnLayout =
      !!element.layout?.type ||
      element.className?.includes("flex") ||
      element.className?.includes("grid");
    if (hasOwnLayout) {
      return (
        <header
          className={cn(
            variantClasses,
            interactionClasses,
            element.className,
            layoutClasses,
            spacingClasses,
            gapClasses,
            usesActivationProxy && PARENT_ACTIVATION_HOST_CLASSES,
          )}
          style={activationHostStyle}
          data-cogo-composite-action-host={
            usesActivationProxy ? "true" : undefined
          }
          {...activationDomAttrs}
          {...atomDataAttrs}
          {...sourceDataAttrs}
        >
          {activationProxy}
          {children}
        </header>
      );
    }
    const defaultInner =
      "mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6 lg:px-8";
    const innerCls =
      containerWidth !== Infinity
        ? resolveResponsiveClassName(defaultInner, containerWidth)
        : defaultInner;
    return (
      <header
        className={cn(
          variantClasses,
          interactionClasses,
          element.className,
          usesActivationProxy && PARENT_ACTIVATION_HOST_CLASSES,
        )}
        style={activationHostStyle}
        data-cogo-composite-action-host={
          usesActivationProxy ? "true" : undefined
        }
        {...activationDomAttrs}
        {...atomDataAttrs}
        {...sourceDataAttrs}
      >
        {activationProxy}
        <div
          className={cn(innerCls, layoutClasses, spacingClasses, gapClasses)}
        >
          {children}
        </div>
      </header>
    );
  }

  // Render as <a> for navigate actions (better semantics + accessibility)
  if (isNavigateAction && navigateRoute) {
    if (usesActivationProxy) {
      const semanticTags = new Set([
        "section",
        "header",
        "footer",
        "nav",
        "main",
        "aside",
        "article",
      ]);
      const requestedTag = (element.htmlTag ??
        element.tag ??
        (semanticTags.has(element.type)
          ? element.type
          : "div")) as React.ElementType;
      const Tag = normalizeParentActivationHostTag(requestedTag);
      return (
        <Tag
          className={cn(className, PARENT_ACTIVATION_HOST_CLASSES)}
          style={activationHostStyle}
          data-cogo-composite-action-host="true"
          {...activationDomAttrs}
          {...atomDataAttrs}
          {...sourceDataAttrs}
        >
          {activationProxy}
          {children}
        </Tag>
      );
    }
    return (
      <a
        href={navigateRoute}
        className={className}
        style={hasInlineStyle ? inlineStyle : undefined}
        onClick={handleClick}
        {...activationDomAttrs}
        {...atomDataAttrs}
        {...sourceDataAttrs}
      >
        {children}
      </a>
    );
  }

  const semanticTags = new Set([
    "section",
    "header",
    "footer",
    "nav",
    "main",
    "aside",
    "article",
  ]);
  const requestedTag = (element.htmlTag ??
    element.tag ??
    (semanticTags.has(element.type)
      ? element.type
      : "div")) as React.ElementType;
  const Tag = usesActivationProxy
    ? normalizeParentActivationHostTag(requestedTag)
    : requestedTag;
  if (requestedTag === "textarea" || requestedTag === "input") {
    const SemanticControl = requestedTag;
    return (
      <SemanticControl
        className={className}
        style={hasInlineStyle ? inlineStyle : undefined}
        onClick={handleClick}
        {...domAttributesFor(element, false)}
        {...atomDataAttrs}
        {...sourceDataAttrs}
      />
    );
  }
  return (
    <Tag
      className={cn(
        className,
        usesActivationProxy && PARENT_ACTIVATION_HOST_CLASSES,
      )}
      style={activationHostStyle}
      data-cogo-composite-action-host={usesActivationProxy ? "true" : undefined}
      {...activationDomAttrs}
      {...atomDataAttrs}
      {...sourceDataAttrs}
    >
      {activationProxy}
      {children}
    </Tag>
  );
}

function resolveLayoutClasses(
  layout?: CogoLayout,
  cw: number = Infinity,
): string {
  if (!layout) return "";
  const raw = layout as unknown as Record<string, unknown>;
  const classes: string[] = [];
  const resolve = cw !== Infinity;

  // Helper: emit class with optional responsive prefix.
  // When containerWidth is known, resolve at data level (no prefix).
  // When containerWidth is Infinity (no provider), emit Tailwind prefix for CSS.
  const emit = (bp: string, cls: string) => {
    if (resolve) {
      if (cw >= (TAILWIND_BP[bp] ?? Infinity)) classes.push(cls);
    } else {
      classes.push(`${bp}:${cls}`);
    }
  };

  const align = layout.align ?? (raw.alignItems as string | undefined);
  const justify = layout.justify ?? (raw.justifyContent as string | undefined);
  const gap = layout.gap ?? (raw.gap as string | number | undefined);

  if (layout.type === "flex") {
    classes.push("flex");
    if (layout.direction === "column") classes.push("flex-col");
    if (align) classes.push(`items-${align}`);
    if (justify) classes.push(`justify-${justify}`);
    if (gap != null) classes.push(`gap-${spacingToken(gap)}`);
    if (layout.wrap) classes.push("flex-wrap");
  } else if (layout.type === "grid") {
    classes.push("grid");
    if (gap != null) classes.push(`gap-${spacingToken(gap)}`);
    if (align) classes.push(`items-${align}`);
    const rows = (raw as Record<string, unknown>).rows as number | undefined;
    if (rows) classes.push(`grid-rows-${rows}`);
    if (layout.columns) {
      classes.push(`grid-cols-${layout.columns}`);
      if (layout.responsive) {
        for (const [bp, ov] of Object.entries(layout.responsive)) {
          if (ov.columns != null) emit(bp, `grid-cols-${ov.columns}`);
        }
      }
    }
  }

  // Responsive breakpoint overrides (non-column properties)
  if (layout.responsive) {
    for (const [bp, overrides] of Object.entries(layout.responsive)) {
      if (overrides.direction === "row") emit(bp, "flex-row");
      if (overrides.direction === "column") emit(bp, "flex-col");
      if (overrides.align) emit(bp, `items-${overrides.align}`);
      if (overrides.justify) emit(bp, `justify-${overrides.justify}`);
      if (overrides.gap) emit(bp, `gap-${spacingToken(overrides.gap)}`);
    }
  }

  return classes.join(" ");
}

function normalizeCssLength(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return `${value}px`;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  return trimmed;
}

function normalizeLayoutGap(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return /^-?(?:\d+|\d*\.\d+)(?:px|rem|em|%|vh|vw|vmin|vmax|ch|ex)$/.test(
    trimmed,
  )
    ? trimmed
    : undefined;
}

function normalizeAspectRatio(value: unknown): string {
  const normalized = String(value ?? "")
    .trim()
    .replace(":", "/");
  return normalized === "square" ? "1 / 1" : normalized;
}

function normalizeDisplay(
  value: unknown,
): "flex" | "grid" | "block" | "none" | undefined {
  return value === "flex" ||
    value === "grid" ||
    value === "block" ||
    value === "none"
    ? value
    : undefined;
}

function normalizeGridColumns(value: unknown): number | undefined {
  const columns = Number(value);
  return Number.isInteger(columns) && columns >= 0 ? columns : undefined;
}

type ContainerAlignment =
  | "topLeft"
  | "topCenter"
  | "topRight"
  | "centerLeft"
  | "center"
  | "centerRight"
  | "bottomLeft"
  | "bottomCenter"
  | "bottomRight";

function normalizeAlignment(value: unknown): ContainerAlignment | undefined {
  const values = new Set<ContainerAlignment>([
    "topLeft",
    "topCenter",
    "topRight",
    "centerLeft",
    "center",
    "centerRight",
    "bottomLeft",
    "bottomCenter",
    "bottomRight",
  ]);
  return typeof value === "string" && values.has(value as ContainerAlignment)
    ? (value as ContainerAlignment)
    : undefined;
}

function resolveAlignmentStyle(
  alignment: ContainerAlignment,
  direction: CogoLayout["direction"] = "column",
): Pick<React.CSSProperties, "alignItems" | "justifyContent"> {
  const [vertical, horizontal] =
    alignment === "center"
      ? ["center", "center"]
      : alignment.startsWith("top")
        ? [
            "flex-start",
            alignment.endsWith("Left")
              ? "flex-start"
              : alignment.endsWith("Right")
                ? "flex-end"
                : "center",
          ]
        : alignment.startsWith("bottom")
          ? [
              "flex-end",
              alignment.endsWith("Left")
                ? "flex-start"
                : alignment.endsWith("Right")
                  ? "flex-end"
                  : "center",
            ]
          : ["center", alignment.endsWith("Left") ? "flex-start" : "flex-end"];

  return direction === "row"
    ? { alignItems: vertical, justifyContent: horizontal }
    : { alignItems: horizontal, justifyContent: vertical };
}

function isOverflowValue(
  value: unknown,
): value is "hidden" | "auto" | "scroll" | "visible" {
  return (
    value === "hidden" ||
    value === "auto" ||
    value === "scroll" ||
    value === "visible"
  );
}

function resolvePaddingStyle(
  padding: string | number | Record<string, unknown> | undefined,
): string | undefined {
  if (padding == null) return undefined;
  if (typeof padding === "number") return `${padding * 4}px`;
  if (typeof padding === "object") {
    const raw = padding as Record<string, unknown>;
    if (raw.all != null) return paddingPart(raw.all);
    const top = paddingPart(raw.top ?? 0);
    const right = paddingPart(raw.right ?? 0);
    const bottom = paddingPart(raw.bottom ?? 0);
    const left = paddingPart(raw.left ?? 0);
    return top && right && bottom && left
      ? `${top} ${right} ${bottom} ${left}`
      : undefined;
  }

  const trimmed = padding.trim();
  if (!trimmed || /^(?:[a-z0-9]+:)*(?:p|px|py|pt|pr|pb|pl)-/.test(trimmed))
    return undefined;
  const parts = trimmed.split(/\s+/);
  if (![1, 2, 3, 4].includes(parts.length)) return undefined;
  const normalized = parts.map(paddingPart);
  return normalized.every(Boolean) ? normalized.join(" ") : undefined;
}

function paddingPart(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value))
    return `${value * 4}px`;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) return `${Number(trimmed) * 4}px`;
  return normalizeCssLength(trimmed);
}

function serializeRuntimeValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : JSON.stringify(value);
}

function dataAttributesFor(element: RendererProps["element"]) {
  const raw = (
    element as {
      dataAttributes?: Record<
        string,
        string | number | boolean | null | undefined
      >;
    }
  ).dataAttributes;
  const attrs: Record<string, string | number | boolean> = {};

  if (!raw) return attrs;

  for (const [rawKey, value] of Object.entries(raw)) {
    if (value == null) continue;
    const key = rawKey.startsWith("data-")
      ? rawKey
      : `data-${rawKey.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}`;
    attrs[key] = value;
  }

  return attrs;
}

function domAttributesFor(
  element: RendererProps["element"],
  suppressParentInteraction = false,
) {
  const attrs: Record<string, string | number | boolean> = {};
  const raw = element as unknown as Record<string, unknown>;
  const htmlId = raw.htmlId;
  if (typeof htmlId === "string" && htmlId.length > 0) attrs.id = htmlId;
  else if (element.id) attrs.id = element.id;
  if (suppressParentInteraction) return attrs;
  if (element.role) attrs.role = element.role;
  if (element.ariaLabel) attrs["aria-label"] = element.ariaLabel;
  if (typeof element.tabIndex === "number") attrs.tabIndex = element.tabIndex;
  const tag = element.htmlTag ?? element.tag;
  if (tag === "button") {
    // Same three placements as button-renderer's resolveHtmlButtonType, same
    // order and same reason — see the comment there. `raw` is this element
    // already widened above.
    const htmlType =
      raw.htmlType ??
      (element.options as Record<string, unknown> | undefined)?.htmlType;
    attrs.type =
      htmlType === "submit" || htmlType === "reset" ? htmlType : "button";
    return attrs;
  }
  if (tag !== "textarea" && tag !== "input") return attrs;
  if (element.placeholder != null) attrs.placeholder = element.placeholder;
  if (element.name != null) attrs.name = element.name;
  const elementValue = element.value;
  if (elementValue != null && !Array.isArray(elementValue)) {
    attrs.defaultValue = elementValue;
  }
  if (element.disabled != null)
    attrs.disabled = element.disabled === true || element.disabled === "true";
  if (element.required != null)
    attrs.required = element.required === true || element.required === "true";
  if (element.readonly != null) attrs.readOnly = element.readonly;
  if (tag === "input" && element.inputType != null)
    attrs.type = element.inputType;
  return attrs;
}
